<template>
  <div class="py-3 min-w-0 w-full">
    <div class="display-1">Kohteiden statistiikka</div>

    <!-- Loader -->
    <full-page-loader
      v-if="pageLoading"
      text="Ladataan statistiikkaa..."
      class="full-page-loader"
    ></full-page-loader>

    <v-card v-if="!pageLoading" class="mt-2">
      <v-card-title>
        <v-btn-toggle mandatory color="primary" v-model="tableName">
          <v-btn value="apartmentStatistics"> Vuokrakohteet </v-btn>
          <v-btn v-if="hasRegionReports" value="cityStatistics"> Kaupungit </v-btn>
        </v-btn-toggle>

        <v-spacer></v-spacer>
        <v-btn
          color="info"
          @click="
            setField({ field: 'editMode', val: false });
            setField({ field: 'openTableHeaderDialog', val: true });
          "
          >Luo taulukko</v-btn
        >
      </v-card-title>

      <v-container fluid>
        <v-row dense v-if="showSearch">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              label="Vuokrakohteen osoite, postinumero tai kaupunki"
              append-icon="mdi-magnify"
              single-line
              hide-details
              solo
              @keyup.enter="getDataFromApi()"
              @click:append="getDataFromApi()"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Select tableheaders -->
        <v-row class="mt-1" dense>
          <v-col cols="12" sm="6" md="4" lg="3" xl="2">
            <select-table-headers></select-table-headers>
          </v-col>
        </v-row>
      </v-container>

      <!-- APARTMENT -->
      <v-data-table
        item-key="_id"
        item-value="id"
        :headers="headers"
        :items="tableData"
        :footer-props="globalValues.footerProps"
        :items-per-page="getDefaultTableRows()"
        :loading="loading"
        :options.sync="options"
        :server-items-length="totalLength"
      >
        <!-- Apartment fixed header -->
        <template #[`item.address`]="{ item }">
          <router-link
            style="text-decoration: none"
            :to="`/apartment/overview/${item.apartmentId}`"
          >
            <div class="font-weight-bold">
              <span
                >{{ item.address
                }}<span v-if="item.apartmentNumber">, {{ item.apartmentNumber }}</span>
              </span>
            </div>
          </router-link>
          <div>{{ item.zipCode }}, {{ item.city }}</div>
        </template>

        <!-- City fixed header-->
        <template #[`item.city`]="{ item }">
          <div class="font-weight-bold">{{ item.city }}</div>
        </template>

        <template v-for="(h, idx) in formattedHeaders" v-slot:[`item.${h.value}`]="{ item }">
          <span v-if="containsHTML(item[h.value])" :key="idx + 'a'" v-html="item[h.value]"> </span>
          <span v-else :key="idx + 'b'"> {{ item[h.value] }}</span>
        </template>

        <template #no-data>
          <h2>Ei vuokrakohteita</h2>
        </template>
      </v-data-table>

      <!-- Create tableheaders -->
      <table-headers-dialog :val="openTableHeaderDialog"></table-headers-dialog>
    </v-card>
  </div>
</template>

<script>
import mixins from "../../mixins/mixins";
import { mapActions, mapState, mapMutations, mapGetters } from "vuex";
import TableHeadersDialog from "@/components/TableHeaders/TableHeadersDialog.vue";
import SelectTableHeaders from "@/components/TableHeaders/SelectTableHeaders.vue";
import FullPageLoader from "@/components/FullPageLoader.vue";
import globalValues from "../../configs/globalValues";
import { getTableData } from "@/utils/tableHelpers";

export default {
  title: "Kohteiden statistiikka",

  components: {
    SelectTableHeaders,
    TableHeadersDialog,
    FullPageLoader,
  },

  mixins: [mixins],

  data: () => ({
    pageLoading: false,
    loading: true,
    options: {},
    search: "",
    globalValues,
  }),

  async created() {
    try {
      this.pageLoading = true;
      await this.$nextTick();
      await this.getTableHeaders(this.tableName);
      this.setUserTableHeader(this.tableName);
      this.pageLoading = false;
    } catch (err) {
      this.pageLoading = false;
      this.showPopup(err, "error");
    }
  },

  computed: {
    ...mapState("report", ["statisticItems", "totalLength"]),
    ...mapState("browsehistory", [
      "dataTablePage",
      "getPreviousPage",
      "itemsPerPage",
      "mainSearchValue",
    ]),
    ...mapState("tableHeader", ["editMode", "openTableHeaderDialog"]),
    ...mapGetters("tableHeader", ["tableHeaders"]),
    ...mapGetters("account", ["hasRegionReports"]),

    tableName: {
      get() {
        return this.$store.state.tableHeader.tableName;
      },
      set(val) {
        this.setField({ field: "tableName", val });
      },
    },

    showSearch() {
      if (this.tableName === "apartmentStatistics") return true;
      return false;
    },

    headers() {
      return this.tableHeaders(this.tableName);
    },

    tableData() {
      return getTableData(this.statisticItems, this.headers, this.tableName);
    },

    formattedHeaders() {
      // From default state headers, filter out the ones that are fixed
      return this.headers.filter((header) => !header.fixed);
    },

    apiEndPointPath() {
      if (this.tableName === "apartmentStatistics") {
        return "apartment-statistics";
      } else if (this.tableName === "cityStatistics") {
        return "city-statistics";
      }
      return "";
    },

    functionToCall() {
      if (this.tableName === "apartmentStatistics") {
        return this.getApartmentStatistics;
      } else {
        return this.getCityStatistics;
      }
    },
  },

  watch: {
    options: {
      handler() {
        if (this.getPreviousPage) {
          if (this.mainSearchValue != "") this.search = this.mainSearchValue;
          this.options.page = this.dataTablePage;
          this.options.itemsPerPage = this.itemsPerPage;
        }
        this.saveTableOptions({ ...this.options });
        this.getDataFromApi();
      },
      deep: true,
    },

    totalLength() {
      this.options.page = 1;
    },

    async tableName(val) {
      try {
        this.setStatisticItems({ items: [], totalLength: 0 });
        await this.getTableHeaders(val);
        this.getDataFromApi();
        this.setUserTableHeader(val);

        if (val === "cityStatistics") this.search = "";
      } catch (err) {
        this.showPopup(err, "error");
      }
    },
  },

  methods: {
    ...mapActions("tableHeader", ["getTableHeaders"]),
    ...mapMutations("tableHeader", ["setField", "setUserTableHeader", "setSelectedHeader"]),
    ...mapActions("report", ["getApartmentStatistics", "getCityStatistics"]),
    ...mapMutations("report", ["setStatisticItems"]),
    ...mapMutations("browsehistory", ["saveTableOptions", "saveMainSearchTerm"]),

    async getDataFromApi() {
      this.loading = true;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;

      // Save main search term
      this.saveMainSearchTerm(this.search);

      let search = this.search.trim().toLowerCase();
      let url = `/api/v1/reports/${this.apiEndPointPath}?sortBy=${sortBy}&sortDesc=${sortDesc}&page=${page}&itemsPerPage=${itemsPerPage}&search=${search}`;
      await this.functionToCall({ url });

      this.loading = false;
    },

    containsHTML(value) {
      // Varmistetaan, että arvo on merkkijono ja että siinä on HTML-tägejä
      if (typeof value !== "string" || !value.includes("<")) return false;

      // Poikkeuksena <br> -tagi, joka ei tarvitse sulkijaa
      const brTags = value.match(/<br\s*\/?>/gi);
      if (brTags && brTags.length > 0) {
        // Jos vain <br> -tagi on yksin, se on hyväksyttävä
        return true;
      }

      // Avattavat ja suljettavat tagit
      const openTags = [
        "strong",
        "b",
        "em",
        "i",
        "u",
        "p",
        "h3",
        "h4",
        "ul",
        "ol",
        "li",
        "span",
        "small",
        "sup",
        "sub",
      ];

      // Tarkistetaan, että kaikilla avatuilla tageilla on vastaava sulkeminen
      for (let tag of openTags) {
        const openTag = new RegExp(`<${tag}[^>]*>`, "g");
        const closeTag = new RegExp(`</${tag}>`, "g");

        let openCount = (value.match(openTag) || []).length;
        let closeCount = (value.match(closeTag) || []).length;

        // Jos avattuja tageja on enemmän kuin suljettuja, palautetaan false
        if (openCount !== closeCount) return false;
      }

      // Tarkistetaan sulkemattomat ja väärin suljetut tagit
      const unclosedTagsRegex = /<(?!br\s*\/?)[a-z][a-z0-9]*\b[^>]*>/gi;
      const tagsInText = value.match(unclosedTagsRegex);

      if (tagsInText) {
        for (let tag of tagsInText) {
          // Jos tagi ei ole yksittäinen sulkeutuva tagi, se on virheellinen
          if (!/<\/?[a-z][a-z0-9]*\b[^>]*>/gi.test(tag)) {
            return false;
          }
        }
      }

      return true;
    },
  },
};
</script>

<style scoped></style>
